// @flow
import type {ApplicationRedux} from 'redux/ApplicationRedux';
import type {VoucherDataState} from 'redux/v1//voucherData/voucherDataState';
import type {BookState} from 'redux/v1/book/BookState';
import type {BookPsdFallbackState} from 'redux/v1/bookPsdFallback/BookPsdFallbackState';
import type {BookStatusState} from 'redux/v1/bookStatus/BookStatusState';
import type {CustomerState} from 'redux/v1/customer/CustomerState';
import type {CustomerNoticeState} from 'redux/v1/customerNotice/CustomerNoticeState';
import type {CustomerVouchersState} from 'redux/v1/customerVouchers/CustomerVouchersState';
import type {FormsheetState} from 'redux/v1/formsheet/FormsheetState';
import type {GeneratedVoucherState} from 'redux/v1/generatedVoucher/GeneratedVoucherState';
import type {ImageState} from 'redux/v1/image/ImageState';
import type {InsuranceState} from 'redux/v1/insurance/InsuranceState';
import type {JourneyState} from 'redux/v1/journey/JourneyState';
import type {OrganizerCancellationState} from 'redux/v1/organizerCancellation/OrganizerCancellationState';
import type {PaymentInsuranceState} from 'redux/v1/paymentInsurance/PaymentInsuranceState';
import type {PaymentTravelState} from 'redux/v1/paymentTravel/PaymentTravelState';
import type {PriceCacheState} from 'redux/v1/priceCache/PriceCacheState';
import type {PsdMessageState} from 'redux/v1/psdMessage/model';
import type {ReservationState} from 'redux/v1/reservation/ReservationState';
import type {ServiceFeedbackState} from 'redux/v1/serviceFeedback/ServiceFeedbackState';
import type {TouroperatorVoucherState} from 'redux/v1/touroperatorVoucher/TouroperatorVoucherState';
import type {TravelersState} from 'redux/v1/travelers/TravelersState';
import type {VoucherState} from 'redux/v1/voucher/VoucherState';

import {v1State} from 'redux/ApplicationRedux';

export type V1State = {
  journey: JourneyState,
  travelers: TravelersState,
  organizerCancellation: OrganizerCancellationState,
  reservation: ReservationState,
  customer: CustomerState,
  customerNotice: CustomerNoticeState,
  formsheet: FormsheetState,
  insurance: InsuranceState,
  paymentTravel: PaymentTravelState,
  paymentInsurance: PaymentInsuranceState,
  book: BookState,
  bookStatus: BookStatusState,
  bookPsdFallback: BookPsdFallbackState,
  psdMessage: PsdMessageState,
  voucher: VoucherState,
  voucherData: VoucherDataState,
  generatedVoucher: GeneratedVoucherState,
  touroperatorVoucher: TouroperatorVoucherState,
  serviceFeedback: ServiceFeedbackState,
  images: ImageState,
  priceCache: PriceCacheState,
  customerVouchers: CustomerVouchersState,
};

export const journeyState = (state: ApplicationRedux): JourneyState =>
  v1State(state).journey;

export const travelerState = (state: ApplicationRedux): TravelersState =>
  v1State(state).travelers;

export const organizerCancellationState = (
  state: ApplicationRedux
): OrganizerCancellationState => v1State(state).organizerCancellation;

export const reservationState = (state: ApplicationRedux): ReservationState =>
  v1State(state).reservation;

export const customerState = (state: ApplicationRedux): CustomerState =>
  v1State(state).customer;

export const customerNoticeState = (
  state: ApplicationRedux
): CustomerNoticeState => v1State(state).customerNotice;

export const formsheetState = (state: ApplicationRedux): FormsheetState =>
  v1State(state).formsheet;

export const insuranceState = (state: ApplicationRedux): InsuranceState =>
  v1State(state).insurance;

export const paymentTravelState = (
  state: ApplicationRedux
): PaymentTravelState => v1State(state).paymentTravel;

export const paymentInsuranceState = (
  state: ApplicationRedux
): PaymentInsuranceState => v1State(state).paymentInsurance;

export const bookState = (state: ApplicationRedux): BookState =>
  v1State(state).book;

export const bookStatusState = (state: ApplicationRedux): BookStatusState =>
  v1State(state).bookStatus;

export const bookPsdFallbackState = (state: ApplicationRedux): BookState =>
  v1State(state).bookPsdFallback;

export const psdMessageState = (state: ApplicationRedux): PsdMessageState =>
  v1State(state).psdMessage;

export const voucherState = (state: ApplicationRedux): VoucherState =>
  v1State(state).voucher;

export const voucherDataState = (state: ApplicationRedux): VoucherDataState =>
  v1State(state).voucherData;

export const generatedVoucherState = (
  state: ApplicationRedux
): GeneratedVoucherState => v1State(state).generatedVoucher;

export const touroperatorVoucherState = (
  state: ApplicationRedux
): TouroperatorVoucherState => v1State(state).touroperatorVoucher;

export const serviceFeedbackState = (
  state: ApplicationRedux
): ServiceFeedbackState => v1State(state).serviceFeedback;

export const imageState = (state: ApplicationRedux): ImageState =>
  v1State(state).images;

export const priceCacheState = (state: ApplicationRedux): PriceCacheState =>
  v1State(state).priceCache;

export const customerVouchersState = (
  state: ApplicationRedux
): CustomerVouchersState => v1State(state).customerVouchers;
